import React, { useState, useEffect } from 'react';
import { ref, onValue, push, update, get } from 'firebase/database';
import { database } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { FaStar, FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import './Avis.css';

const Avis = () => {
  const { currentUser } = useAuth();
  const [servicesTermines, setServicesTermines] = useState([]);
  const [mesAvis, setMesAvis] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [selectedService, setSelectedService] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState('');
  const [note, setNote] = useState(0);
  const [commentaire, setCommentaire] = useState('');
  const [hover, setHover] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [avisSansRdv, setAvisSansRdv] = useState(true); // Changé: true par défaut (avis général en premier)

  // Charger les informations de l'utilisateur
  useEffect(() => {
    if (!currentUser) return;

    const userRef = ref(database, `utilisateurs/${currentUser.uid}`);
    const unsubscribe = onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUserInfo(data);
      } else {
        setUserInfo({
          nomComplet: currentUser.email?.split('@')[0],
          prenom: currentUser.email?.split('@')[0],
          nom: ''
        });
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Charger les services terminés (avec ou sans rendez-vous)
  useEffect(() => {
    if (!currentUser) return;

    const rendezvousRef = ref(database, `rendezvous/${currentUser.uid}`);
    
    const unsubscribe = onValue(rendezvousRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const servicesList = Object.entries(data).map(([id, value]) => ({
          id,
          ...value
        }));
        
        // Filtrer les services terminés sans avis
        const termines = servicesList.filter(
          s => s.statut === 'terminé' && !s.avisDonne
        );
        setServicesTermines(termines);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Charger les avis déjà donnés par l'utilisateur
  useEffect(() => {
    if (!currentUser) return;

    const avisRef = ref(database, 'avis');
    const unsubscribe = onValue(avisRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const mesAvisList = Object.entries(data)
          .map(([id, avis]) => ({
            id,
            ...avis
          }))
          .filter(avis => avis.userId === currentUser.uid)
          .sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setMesAvis(mesAvisList);
      }
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (note === 0) {
      alert('Veuillez donner une note');
      return;
    }

    if (!commentaire.trim()) {
      alert('Veuillez écrire un commentaire');
      return;
    }

    try {
      const nomClient = userInfo?.nomComplet || userInfo?.prenom || currentUser.email?.split('@')[0];
      const prenomClient = userInfo?.prenom || '';
      const nomClientFamille = userInfo?.nom || '';
      
      const avisRef = ref(database, 'avis');
      const newAvisRef = push(avisRef);
      
      // Construction de l'objet avis
      const avisData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: nomClient,
        userPrenom: prenomClient,
        userNom: nomClientFamille,
        note,
        commentaire,
        date: new Date().toISOString(),
        valide: false,
        sansRdv: avisSansRdv
      };

      // Si un service est sélectionné, ajouter ses infos
      if (selectedServiceId && !avisSansRdv) {
        const service = servicesTermines.find(s => s.id === selectedServiceId);
        avisData.serviceId = selectedServiceId;
        avisData.serviceType = service?.typeService || 'Non spécifié';
        avisData.dateService = service?.date || new Date().toISOString();
        
        // Marquer le service comme ayant un avis
        const serviceRef = ref(database, `rendezvous/${currentUser.uid}/${selectedServiceId}`);
        await update(serviceRef, { avisDonne: true });
      } else {
        avisData.serviceType = 'Avis général';
        avisData.sansRdv = true;
      }

      await update(newAvisRef, avisData);

      setSubmitted(true);
      setSelectedService('');
      setSelectedServiceId('');
      setNote(0);
      setCommentaire('');
      setAvisSansRdv(true); // Rester sur l'avis général après envoi
      
      setTimeout(() => setSubmitted(false), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'envoi de l\'avis:', error);
      alert('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  const handleServiceSelect = (e) => {
    const serviceId = e.target.value;
    setSelectedServiceId(serviceId);
    if (serviceId) {
      setAvisSansRdv(false);
      const service = servicesTermines.find(s => s.id === serviceId);
      setSelectedService(service ? `${new Date(service.date).toLocaleDateString('fr-FR')} - ${service.typeService}` : '');
    } else {
      setSelectedService('');
    }
  };

  const handleAvisSansRdv = () => {
    setAvisSansRdv(true);
    setSelectedServiceId('');
    setSelectedService('');
  };

  const handleAvecRdv = () => {
    setAvisSansRdv(false);
    setSelectedServiceId('');
    setSelectedService('');
  };

  if (loading) {
    return (
      <div className="avis-container">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="avis-container">
      <h2>Donner mon avis</h2>
      
      {/* Option pour avis - Avis général en premier */}
      <div className="avis-options">
        <button 
          className={`option-btn ${avisSansRdv ? 'active' : ''}`}
          onClick={handleAvisSansRdv}
        >
          ⭐ Avis général
        </button>
        <button 
          className={`option-btn ${!avisSansRdv ? 'active' : ''}`}
          onClick={handleAvecRdv}
        >
          📦 Avis sur un service existant
        </button>
      </div>

      {!avisSansRdv && servicesTermines.length === 0 ? (
        <div className="no-data">
          <p>🎉 Aucun service terminé à évaluer pour le moment.</p>
          <p className="small">Vous pouvez laisser un avis général sur notre service en cliquant sur "Avis général" ci-dessus.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="avis-form">
          {!avisSansRdv && servicesTermines.length > 0 && (
            <div className="form-group">
              <label>Sélectionnez un service à évaluer</label>
              <select
                value={selectedServiceId}
                onChange={handleServiceSelect}
                required={!avisSansRdv}
                className="service-select"
              >
                <option value="">Choisissez un service</option>
                {servicesTermines.map(service => (
                  <option key={service.id} value={service.id}>
                    📅 {new Date(service.date).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })} - 🚚 {service.typeService === 'maison' ? 'Déménagement maison' :
                              service.typeService === 'bureau' ? 'Déménagement bureau' :
                              service.typeService === 'meubles' ? 'Transport de meubles' :
                              'Transport longue distance'}
                  </option>
                ))}
              </select>
            </div>
          )}

          {avisSansRdv && (
            <div className="info-message">
              <FaInfoCircle />
              <p>Vous laissez un avis général sur notre service. Merci pour votre retour !</p>
            </div>
          )}

          <div className="form-group">
            <label>Votre note</label>
            <div className="star-rating">
              {[...Array(5)].map((_, index) => {
                const ratingValue = index + 1;
                return (
                  <label key={index} className="star-label">
                    <input
                      type="radio"
                      name="rating"
                      value={ratingValue}
                      onClick={() => setNote(ratingValue)}
                      style={{ display: 'none' }}
                    />
                    <FaStar
                      className="star"
                      color={ratingValue <= (hover || note) ? '#ffc107' : '#e4e5e9'}
                      size={32}
                      onMouseEnter={() => setHover(ratingValue)}
                      onMouseLeave={() => setHover(0)}
                      style={{ cursor: 'pointer', transition: 'transform 0.2s' }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  </label>
                );
              })}
            </div>
            <p className="note-indicator">
              {note === 1 && '😞 Très déçu'}
              {note === 2 && '😕 Peu satisfait'}
              {note === 3 && '😐 Satisfait'}
              {note === 4 && '😊 Très satisfait'}
              {note === 5 && '🌟 Excellent !'}
            </p>
          </div>

          <div className="form-group">
            <label>Votre commentaire</label>
            <textarea
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              rows="5"
              required
              placeholder="Partagez votre expérience avec nous..."
              className="comment-textarea"
            />
          </div>

          <button type="submit" className="btn-submit">
            <FaStar style={{ marginRight: '0.5rem' }} />
            Envoyer mon avis
          </button>
          
          {submitted && (
            <div className="success-message">
              ✅ Merci pour votre avis ! Il sera visible après validation par notre équipe.
            </div>
          )}
        </form>
      )}

      {/* Mes avis précédents */}
      {mesAvis.length > 0 && (
        <div className="mes-avis">
          <h3>📝 Mes avis précédents</h3>
          <div className="avis-list">
            {mesAvis.map((avis) => (
              <div key={avis.id} className="avis-card">
                <div className="avis-header">
                  <div className="avis-service">
                    <span className="service-badge">
                      {avis.sansRdv ? '⭐ Avis général' : avis.serviceType}
                    </span>
                    <span className="avis-date">{new Date(avis.date).toLocaleDateString('fr-FR')}</span>
                  </div>
                  <div className={`status-badge ${avis.valide ? 'valide' : 'en-attente'}`}>
                    {avis.valide ? '✓ Validé' : '⏳ En attente'}
                  </div>
                </div>
                
                <div className="avis-rating">
                  {[...Array(5)].map((_, i) => (
                    <FaStar 
                      key={i} 
                      className={i < avis.note ? 'star-filled' : 'star-empty'} 
                    />
                  ))}
                </div>
                
                <p className="avis-commentaire">"{avis.commentaire}"</p>
                
                {!avis.valide && (
                  <p className="avis-warning">
                    Cet avis est en attente de validation par notre équipe.
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Avis;